import React, { useEffect, useState, useCallback } from 'react';
import { EntityFilter, useEntityList, catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import FilterFormControlComponent from './FilterFormControlComponent';
import { CustomDropdownFilterConfig } from './CustomDropdownFilterConfig';
import { consumerDisplayNames } from '../../utils/helpers';
import { EraserButton } from '../EraserButton/EraserButton';
import { Tooltip } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setFilter, clearPageFilters, selectAllFilters } from '../../redux/filterSlice';
import { clearAllSelections } from '../../redux/serviceOwnersSlice';
import {
  setActiveChart,
  setSelectedEntity,
  setActiveData,
  setChartFilteredEntities,
} from '../../redux/integrationsExplorerSlice';

export const toTitleCase = (str: string): string => {
  if (!str) {
    return '';
  }
  const isAcronym = (word: string) => {
    return word.length > 1 && word === word.toUpperCase();
  };
  return str
    .split(/(\s+|-)/)
    .map(word => {
      if (word.match(/^\s+$/) || word === '-') {
        return word;
      }
      if (isAcronym(word)) {
        return word;
      } else {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
    })
    .join('');
};

const getValueFromPath = (obj: any, path: string): any | undefined => {
  try {
    const pathArray: string[] = [];

    if (path !== 'relations.dependencyOf') {
      const parts = path?.split('.');
      for (let i = 0; i < parts.length; i++) {
        if (parts[i] === 'backstage' && parts[i + 1]?.startsWith('io')) {
          pathArray.push(`${parts[i]}.${parts[i + 1]}`);
          i++;
        } else {
          pathArray.push(parts[i]);
        }
      }
      return pathArray.reduce((o, i) => (o ? o[i] : undefined), obj);
    }
    else {
      const result = obj.relations
        .filter((relation: any) => relation.type === 'dependencyOf')
        .map((relation: any) => {
          if (relation.targetRef.split(':')[0] === 'component') {
            return relation.targetRef;
          }
          return undefined;
        })
        .filter((tag: any) => tag !== undefined)
      return result
    }
  } catch (error) {
    return undefined;
  }
};

export class CustomFilterImpl implements EntityFilter {
  constructor(readonly value: string[], readonly annotation: string) { }

  getCatalogFilters(): Record<string, string | string[]> {
    return this.value.length > 0 ? { [`${this.annotation}`]: this.value } : {};
  }

  filterEntity(entity: Entity): boolean {
    const items = getValueFromPath(entity, this.annotation);
    const uppercaseItems = Array.isArray(items)
      ? items.map(o => String(o).toUpperCase())
      : String(items).toUpperCase();
    return (
      uppercaseItems !== undefined &&
      this.value.some(val => uppercaseItems.includes(String(val).toUpperCase()))
    );
  }
}

const extractUniqueOptions = (
  annotation: string,
  entities: Entity[],
): string[] => {
  return Array.from(
    new Set(
      entities
        .flatMap(entity => getValueFromPath(entity, annotation))
        .filter(owner => owner !== undefined && owner !== ''),
    ),
  ) as string[];
};

const normalizeEntityValues = (entity: Entity, annotation: string): string[] => {
  const items = getValueFromPath(entity, annotation);
  if (items === undefined || items === null) {
    return [];
  }
  return Array.isArray(items) ? items.map(item => String(item)) : [String(items)];
};

const dedupeValues = (values: string[]): string[] => {
  const seen = new Set<string>();
  return values.filter(value => {
    const normalized = String(value).trim().toUpperCase();
    if (!normalized || seen.has(normalized)) {
      return false;
    }
    seen.add(normalized);
    return true;
  });
};

const areSameValues = (left: string[] = [], right: string[] = []): boolean => {
  const normalize = (values: string[]) =>
    dedupeValues(values)
      .map(value => String(value).trim().toUpperCase())
      .sort();

  const normalizedLeft = normalize(left);
  const normalizedRight = normalize(right);

  return JSON.stringify(normalizedLeft) === JSON.stringify(normalizedRight);
};

const toDisplayOptions = (values: string[]): string[] => {
  return dedupeValues(values.map(value => toTitleCase(String(value))));
};

type CustomDropdownFilterProps = {
  filterConfig?: typeof CustomDropdownFilterConfig;
  page: string;
  isMobile?: boolean;
  flag?: boolean;
};

const CustomDropdownFilter = ({
  filterConfig = CustomDropdownFilterConfig,
  page,
  isMobile,
  flag = true,
}: CustomDropdownFilterProps) => {
  const { updateFilters, entities } = useEntityList();
  const catalogApi = useApi(catalogApiRef);
  const dispatch = useAppDispatch();
  const allFilters = useAppSelector(selectAllFilters);
  const [allApiEntities, setAllApiEntities] = useState<Entity[]>([]);
  const [componentEntities, setComponentEntities] = useState<any[]>([]);
  const [cachedOptions, setCachedOptions] = useState<Record<string, string[]>>({});
  const [componentEntitiesLoaded, setComponentEntitiesLoaded] = useState(false);

  const filterStates = filterConfig.configs.map(() => ({
    selectedOptions: useState<string[]>([]),
    inputValue: useState<string>(''),
  }));

  const applyCatalogFilter = useCallback((tag: string, value: string[]) => {
    const dedupedValue = dedupeValues(value);
    if (tag === 'relations.dependencyOf') {
      if (!flag) {
        const consumerName = dedupedValue.map(t => {
          const matchingEntity = Object.entries(consumerDisplayNames).find(([, val]) => val === t);
          return matchingEntity ? matchingEntity[0] : null;
        }).filter(Boolean) as string[];

        updateFilters({ [tag]: consumerName.length > 0 ? new CustomFilterImpl(consumerName, tag) : undefined } as any);
      }
      else {
        const consumerName = dedupedValue.map(t => {
          const matchingEntity = componentEntities.find(entity => entity.metadata.title?.toLowerCase() === t?.toLowerCase());
          if (matchingEntity) {
            return `component:default/${matchingEntity.metadata.name}`;
          }
          return null;
        }).filter(Boolean) as string[];
        updateFilters({ [tag]: consumerName.length > 0 ? new CustomFilterImpl(consumerName, tag) : undefined } as any);
      }
    }
    else {
      updateFilters({
        [tag]: dedupedValue.length > 0 ? new CustomFilterImpl(dedupedValue, tag) : undefined,
      } as any);
    }
  }, [updateFilters, flag, componentEntities]);

  const handleFiltering = useCallback((tag: string) => (value: string[]) => {
    const dedupedValue = dedupeValues(value);
    dispatch(setFilter({ key: `${page}-${tag}`, value: dedupedValue }));
    applyCatalogFilter(tag, dedupedValue);
  }, [dispatch, page, applyCatalogFilter]);

  const mapSelectedValuesToEntityValues = useCallback((annotation: string, selectedValues: string[]) => {
    if (annotation !== 'relations.dependencyOf') {
      return selectedValues;
    }

    if (!flag) {
      return selectedValues.map(value => {
        const matchingEntity = Object.entries(consumerDisplayNames).find(([, displayName]) => displayName === value);
        return matchingEntity ? matchingEntity[0] : value;
      });
    }

    return selectedValues.map(value => {
      if (value.includes(':')) {
        return value;
      }
      const matchingEntity = componentEntities.find(entity => entity.metadata.title?.toLowerCase() === value?.toLowerCase());
      if (matchingEntity) {
        return `component:default/${matchingEntity.metadata.name}`;
      }
      return value;
    });
  }, [flag, componentEntities]);

  const entityMatchesSelection = useCallback((entity: Entity, annotation: string, selectedValues: string[]) => {
    if (!selectedValues?.length) {
      return true;
    }

    const normalizedSelectedValues = mapSelectedValuesToEntityValues(annotation, selectedValues)
      .map(value => String(value).toUpperCase());
    const entityValues = normalizeEntityValues(entity, annotation)
      .map(value => value.toUpperCase());

    return normalizedSelectedValues.some(selected => entityValues.includes(selected));
  }, [mapSelectedValuesToEntityValues]);

  useEffect(() => {
    if (entities.length === 0) return;
    const fetchComponentEntities = async () => {
      try {
        const response = await catalogApi.getEntities();
        const allEntities = response.items as Entity[];
        const components = allEntities.filter(entity => entity.kind === 'Component');
        const apis = allEntities.filter(entity => entity.kind === 'API');
        setComponentEntities(components);
        setAllApiEntities(apis);
      } catch (error) {
        console.error('Error fetching component entities:', error);
      }
      finally {
        setComponentEntitiesLoaded(true);
      }
    };
    fetchComponentEntities();
  }, [catalogApi, entities]);

  useEffect(() => {
    const filteredEntitiesByKind = allApiEntities.length > 0
      ? allApiEntities
      : entities.filter(entity => entity.kind === 'API');

    const selectedValuesByAnnotation = filterConfig.configs.reduce((acc, config) => {
      const filterKey = `${page}-${config.annotation}`;
      const selectedValues = allFilters[filterKey];
      if (Array.isArray(selectedValues) && selectedValues.length > 0) {
        acc[config.annotation] = selectedValues;
      }
      return acc;
    }, {} as Record<string, string[]>);

    const optionsMap: Record<string, string[]> = {};

    filterConfig.configs.forEach(config => {
      const filteredByOtherSelections = filteredEntitiesByKind.filter(entity => {
        return filterConfig.configs.every(otherConfig => {
          if (otherConfig.annotation === config.annotation) {
            return true;
          }

          const selectedValues = selectedValuesByAnnotation[otherConfig.annotation] || [];
          return entityMatchesSelection(entity, otherConfig.annotation, selectedValues);
        });
      });

      let uniqueOpts = dedupeValues(extractUniqueOptions(config.annotation, filteredByOtherSelections));

      if (config.annotation === 'relations.dependencyOf') {
        if (flag) {
          const filteredOptions = uniqueOpts.reduce((opts: string[], option) => {
            const extractedOption = option.split(':')[1]?.split('/')[1];
            if (extractedOption) {
              opts.push(extractedOption);
            }
            return opts;
          }, []);

          uniqueOpts = filteredOptions.map(option => {
            const matchingEntity = componentEntities.find(entity => entity.metadata.name === option);
            return matchingEntity?.metadata.title || option;
          });
        } else {
          uniqueOpts = uniqueOpts.reduce((opts: string[], option) => {
            const displayName = (consumerDisplayNames as Record<string, string>)[option];
            if (displayName) {
              opts.push(displayName);
            }
            return opts;
          }, []);
        }
      }

      const selectedInCurrentFilter = dedupeValues(selectedValuesByAnnotation[config.annotation] || []);
      optionsMap[config.annotation] = dedupeValues([...uniqueOpts, ...selectedInCurrentFilter]);
    });

    setCachedOptions(optionsMap);
  }, [allApiEntities, entities, filterConfig, page, allFilters, entityMatchesSelection, flag, componentEntities]);
  useEffect(() => {
    filterConfig.configs.forEach((config, index) => {
      const filterKey = `${page}-${config.annotation}`;
      const savedOptions = allFilters[filterKey];
      if (!Array.isArray(savedOptions)) {
        return;
      }
      const [currentLocalOptions] = filterStates[index].selectedOptions;
      
      // Skip restoration of External Consumers filter if component entities haven't loaded yet
      if (config.annotation === 'relations.dependencyOf' && !componentEntitiesLoaded) {
        return;
      }
      
      if (!areSameValues(savedOptions, currentLocalOptions)) {
        const dedupedSavedOptions = dedupeValues(savedOptions);
        const setSelectedOptions = filterStates[index].selectedOptions[1];
        setSelectedOptions(dedupedSavedOptions);
        applyCatalogFilter(config.annotation, dedupedSavedOptions);
      }
    });
  }, [allFilters, page, filterConfig, componentEntitiesLoaded, applyCatalogFilter]);
  const handleClearAll = () => {
    dispatch(clearPageFilters({ page }));
    if (page === 'serviceOwner') {
      dispatch(clearAllSelections());
    }
    filterStates.forEach((state, index) => {
      state.selectedOptions[1]([]);
      state.inputValue[1]('');
      const config = filterConfig.configs[index];
      handleFiltering(config.annotation)([]);
    });
    dispatch(setActiveChart(''));
    dispatch(setSelectedEntity(null));
    dispatch(setActiveData(''));
    dispatch(setChartFilteredEntities(null));
  };

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#888' }}>Filters</div>
        <Tooltip title="Clear All">
          <div>
            <EraserButton
              setSelectedOptions={handleClearAll}
              setInputValue={handleClearAll}
              onChange={handleClearAll}
              imagePath="M 6.496094 1 C 5.675781 1 5 1.675781 5 2.496094 L 5 3 L 2 3 L 2 4 L 3 4 L 3 12.5 C 3 13.324219 3.675781 14 4.5 14 L 10.5 14 C 11.324219 14 12 13.324219 12 12.5 L 12 4 L 13 4 L 13 3 L 10 3 L 10 2.496094 C 10 1.675781 9.324219 1 8.503906 1 Z M 6.496094 2 L 8.503906 2 C 8.785156 2 9 2.214844 9 2.496094 L 9 3 L 6 3 L 6 2.496094 C 6 2.214844 6.214844 2 6.496094 2 Z M 4 4 L 11 4 L 11 12.5 C 11 12.78125 10.78125 13 10.5 13 L 4.5 13 C 4.21875 13 4 12.78125 4 12.5 Z M 5 5 L 5 12 L 6 12 L 6 5 Z M 7 5 L 7 12 L 8 12 L 8 5 Z M 9 5 L 9 12 L 10 12 L 10 5 Z" />
          </div>
        </Tooltip>
      </div>
      <div style={{ maxHeight: '62vh', overflowY: 'auto', scrollBehavior: 'smooth' }}>
        {filterConfig.configs.map((config, index) => (
          <div key={config.annotation}>
            <FilterFormControlComponent
              isMobile={isMobile}
              options={toDisplayOptions(cachedOptions[config.annotation] || [])}
              selectedOptions={filterStates[index].selectedOptions[0]}
              inputValue={filterStates[index].inputValue[0]}
              setSelectedOptions={filterStates[index].selectedOptions[1]}
              setInputValue={filterStates[index].inputValue[1]}
              onChange={handleFiltering(config.annotation)}
              label={config.label}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomDropdownFilter;